import { useCanManageCompany } from "@/hooks/useCanManageCompany";
import { useState, useEffect, useCallback } from "react";
import { Plus, Trash2, Save, FileText } from "lucide-react";
import { useIsSuperadmin } from "@/hooks/useUserRole";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useCurrentCompany } from "@/hooks/useCurrentCompany";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const EXAMPLE_RULES = JSON.stringify(
  [
    {
      name: "Miele Standard",
      conditions: [
        { field: "hersteller", operator: "equals", value: "Miele" },
        { field: "alter_jahre", operator: "less_than", value: 8 },
        { field: "modell", operator: "not_in", value: ["W123", "W456"] },
      ],
    },
  ],
  null,
  2,
);

interface CompanyRule {
  id: string;
  company_id: string;
  name: string;
  rules_json: any;
  created_at: string;
  updated_at: string;
}

export default function DeviceRules() {
  const { t } = useTranslation("admin");
  const { allowed: isAdmin, isLoading: authLoading } = useCanManageCompany();
  const { isSuperadmin: hasSuperadminRole } = useIsSuperadmin();
  const { user } = useAuth();
  const { id: companyId, loading: companyLoading } = useCurrentCompany();
  const navigate = useNavigate();

  const [rules, setRules] = useState<CompanyRule[]>([]);
  const [editStates, setEditStates] = useState<Record<string, { name: string; json: string }>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);

  const fetchRules = useCallback(async (cId: string) => {
    const { data, error } = await supabase
      .from("company_rules")
      .select("*")
      .eq("company_id", cId)
      .order("created_at");
    if (error) {
      toast.error("Failed to load rules");
      return;
    }
    const rulesData = (data || []) as CompanyRule[];
    setRules(rulesData);
    const states: Record<string, { name: string; json: string }> = {};
    rulesData.forEach((r) => {
      states[r.id] = { name: r.name, json: JSON.stringify(r.rules_json, null, 2) };
    });
    setEditStates(states);
  }, []);

  useEffect(() => {
    if ((!isAdmin && !hasSuperadminRole) || !user || companyLoading) return;
    (async () => {
      setIsLoading(true);
      if (companyId) await fetchRules(companyId);
      setIsLoading(false);
    })();
  }, [isAdmin, hasSuperadminRole, user, companyId, companyLoading, fetchRules]);


  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }
  // Access enforced by the route guard in App.tsx.

  const handleAdd = async () => {
    if (!companyId) {
      toast.error("No company assigned");
      return;
    }
    const { data, error } = await supabase
      .from("company_rules")
      .insert({ company_id: companyId, name: "Neue Regel", rules_json: JSON.parse(EXAMPLE_RULES) })
      .select()
      .single();
    if (error) {
      toast.error("Failed to create rule");
      return;
    }
    const newRule = data as CompanyRule;
    setRules((prev) => [...prev, newRule]);
    setEditStates((prev) => ({
      ...prev,
      [newRule.id]: { name: newRule.name, json: JSON.stringify(newRule.rules_json, null, 2) },
    }));
    toast.success("Rule created");
  };

  const handleSave = async (ruleId: string) => {
    const state = editStates[ruleId];
    if (!state) return;

    let parsedJson;
    try {
      parsedJson = JSON.parse(state.json);
    } catch {
      toast.error("Invalid JSON format");
      return;
    }

    setSaving(ruleId);
    const { error } = await supabase
      .from("company_rules")
      .update({ name: state.name, rules_json: parsedJson })
      .eq("id", ruleId);
    setSaving(null);

    if (error) {
      toast.error("Failed to save rule");
      return;
    }
    toast.success("Rule saved");
    if (companyId) fetchRules(companyId);
  };

  const handleDelete = async (ruleId: string) => {
    if (!confirm("Regel wirklich löschen?")) return;
    const { error } = await supabase.from("company_rules").delete().eq("id", ruleId);
    if (error) {
      toast.error("Failed to delete rule");
      return;
    }
    setRules((prev) => prev.filter((r) => r.id !== ruleId));
    toast.success("Rule deleted");
  };

  return (
    <div className="min-h-screen bg-background pb-24">
      <div className="px-6 pt-8 pb-6">
        <div className="flex items-center gap-2 mb-1">
          <FileText className="w-5 h-5 text-primary" />
          <h1 className="text-2xl font-bold text-foreground">{t("deviceRules.title")}</h1>
        </div>
        <p className="text-muted-foreground">{t("deviceRules.subtitle")}</p>
      </div>

      <div className="px-6 space-y-4">
        <div className="flex justify-end">
          <Button className="gap-2" onClick={handleAdd} disabled={!companyId}>
            <Plus className="w-4 h-4" />
            {t("deviceRules.addRule")}
          </Button>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
          </div>
        ) : !companyId ? (
          <div className="text-center py-12 text-muted-foreground">
            You are not assigned to any company. Please contact an administrator.
          </div>
        ) : rules.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            No rules yet. Add one to get started.
          </div>
        ) : (
          rules.map((rule) => {
            const state = editStates[rule.id] || { name: rule.name, json: "" };
            return (
              <div key={rule.id} className="bg-card rounded-xl border border-border p-5 space-y-3">
                <Input
                  value={state.name}
                  onChange={(e) =>
                    setEditStates((prev) => ({
                      ...prev,
                      [rule.id]: { ...prev[rule.id], name: e.target.value },
                    }))
                  }
                  placeholder="Rule name"
                  className="font-medium"
                />
                <Textarea
                  value={state.json}
                  onChange={(e) =>
                    setEditStates((prev) => ({
                      ...prev,
                      [rule.id]: { ...prev[rule.id], json: e.target.value },
                    }))
                  }
                  rows={10}
                  className="font-mono text-xs"
                  placeholder="Rules JSON"
                />
                <div className="flex gap-2 justify-end">
                  <Button
                    variant="destructive"
                    size="sm"
                    className="gap-1"
                    onClick={() => handleDelete(rule.id)}
                  >
                    <Trash2 className="w-4 h-4" />
                    Delete
                  </Button>
                  <Button
                    size="sm"
                    className="gap-1"
                    onClick={() => handleSave(rule.id)}
                    disabled={saving === rule.id}
                  >
                    <Save className="w-4 h-4" />
                    {saving === rule.id ? "Saving..." : "Save"}
                  </Button>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
